import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollTo } from "./Navbar";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroSection = ({ t, lang }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden grain">
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, var(--brass) 0%, transparent 65%)" }}
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full pt-32 pb-20 md:pt-40 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-12 relative z-10 max-w-4xl" key={lang}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-brass mb-8"
          >
            {t.hero.overline}
          </motion.p>
          <h1 data-testid="hero-headline" className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight text-ink">
            <MaskedLine delay={0.25}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.38} className="italic text-brass">{t.hero.line2}</MaskedLine>
            <MaskedLine delay={0.51}>{t.hero.line3}</MaskedLine>
          </h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="origin-left h-px bg-brass w-40 sm:w-64 my-8"
          />
          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="text-base sm:text-lg text-ink2 leading-relaxed max-w-xl"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <button
              data-testid="hero-cta-contact-button"
              onClick={() => scrollTo("#contact")}
              className="group inline-flex items-center gap-2 bg-ink text-paper px-7 py-3.5 rounded-full text-sm font-medium tracking-wide hover:bg-brass transition-colors duration-300"
            >
              {t.hero.cta1}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-filmography-button"
              onClick={() => scrollTo("#fiction")}
              className="inline-flex items-center gap-2 border border-linestrong text-ink px-7 py-3.5 rounded-full text-sm font-medium tracking-wide hover:border-ink transition-colors duration-300"
            >
              {t.hero.cta2}
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="flex flex-wrap gap-x-6 gap-y-2 mt-12"
          >
            {[t.hero.badge1, t.hero.badge2, t.hero.badge3].map((b) => (
              <span key={b} className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink3">
                {b}
              </span>
            ))}
          </motion.div>
        </motion.div>

      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        onClick={() => scrollTo("#facilitateur")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink3 hover:text-ink transition-colors"
        aria-label={t.hero.scroll}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.button>
    </section>
  );
};
